/**
 * Auth service — the single seam between our UI and the backend's auth system.
 *
 * Wired to the landed JSON auth contract (Alejandro, 2026-06-11 Postman
 * collection). All endpoints live under `/api/users/`:
 *   POST /signup/                 { email, first_name, last_name, password1, password2 }
 *   POST /login/                  { username, password } -> { status, otp_hash }  (no token)
 *   POST /login/otp/              { otp_hash, otp }       -> sets `sessionid` cookie (no token)
 *   POST /logout/                 (empty)
 *   POST /password-reset/         { email }
 *   POST /password-reset/confirm/ { uid, token, new_password1, new_password2 }
 *
 * The bearer is NEVER returned in an auth response body. login + OTP establish a
 * Django session (the `sessionid` cookie is set at the OTP step); afterwards
 * `bootstrapSessionToken()` reads the token from `GET /info/.user.token`. This
 * refines ADR-004 — the SPA fetches the token via /info/, it is not DOM-injected
 * like the old Django-served app. The cookie-bearing calls (login/otp/info) send
 * `withCredentials: true` so the session cookie rides same-site; every later call
 * uses the Bearer (`withCredentials: false`, base_service default).
 *
 * `bootstrapToken()` + `readInjectedToken()` remain as a DOM-injection / dev
 * `VITE_API_TOKEN` seed fallback (base_service) for app boot. Supabase is removed
 * from the auth surface here; full `@supabase/supabase-js` removal is still gated
 * on the account-profile edits + the monitoring rewrite (no JSON endpoints in this
 * collection). See findings_2026-06-11 + ADR-010.
 */
import { apiEndpoints } from "@/src/config/api/endpoints";

import {
  api,
  getApiBaseUrl,
  getAuthToken,
  setAuthToken,
  clearAuthToken,
  resetApiConfig,
} from "./base_service";
import { clearBatchFilenameCache } from "./batch_service";
import { stopMonitoringProcessingPoll } from "./monitoring_processing_store";
import { invalidateSearchConfigurationsCache } from "./search_configurations_service";
import type { ApiError, Result } from "./types";

/** The literal token-data value index.html ships with (i.e. no real token). */
export const INJECTED_TOKEN_PLACEHOLDER = "your-staging-token-here";

/** The backend's hosted login page — the real auth entry point (+ email OTP). */
export function getLoginUrl(): string {
  return `${getApiBaseUrl().replace(/\/+$/, "")}/users/login/`;
}

export type SsoOauthProvider =
  | "google"
  | "github"
  | "microsoft"
  | "okta"
  | "google_enterprise"
  | "okta_enterprise";

export function getSsoLoginUrl(provider: SsoOauthProvider): string {
  const base = getApiBaseUrl().replace(/\/+$/, "");
  if (provider === "google_enterprise") {
    return `${base}/users/google/organizations/?process=login`;
  }
  if (provider === "okta_enterprise") {
    return `${base}/users/okta/organizations/?process=login`;
  }
  return `${base}/accounts/${provider}/login/?process=login&next=%2Fusers%2Fauth_continuation%2F`;
}

/**
 * Read the bearer the backend injected into `#root[token-data]`, exactly like
 * the ref's getAPIKey() — but non-throwing, treating the build-time placeholder
 * or an empty value as "absent".
 */
export function readInjectedToken(): string | null {
  if (typeof document === "undefined") return null;
  const token = document.getElementById("root")?.getAttribute("token-data");
  if (!token || token === INJECTED_TOKEN_PLACEHOLDER) return null;
  return token;
}

/**
 * Resolve the bearer at app boot: a DOM-injected token wins (freshest, mirrors
 * the ref), else an already-set store token (the dev VITE_API_TOKEN seed), else
 * null. Adopts an injected token into the per-request store. Returns the token,
 * or null (null → the caller redirects to the hosted login).
 */
export function bootstrapToken(): string | null {
  const injected = readInjectedToken();
  if (injected) {
    setAuthToken(injected);
    return injected;
  }
  return getAuthToken() ?? null;
}

/** True when a bearer is present (a standalone SPA's notion of "logged in"). */
export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

/** Send the browser to the backend's hosted login page. */
export function redirectToLogin(): void {
  if (typeof window !== "undefined") window.location.assign(getLoginUrl());
}

// ---------------------------------------------------------------------------
// JSON login flow — CONFIRMED against staging (Alejandro, 2026-06-09):
//   POST /api/users/login/      { username, password }
//        -> { status: "otp_required", otp_hash }   (email-OTP accounts)
//        -> { status: "authenticated" }            (OTP-disabled accounts)
//   POST /api/users/login/otp/  { otp_hash, otp }  -> session established
//   GET  /info/                 -> { user: { token } }   <-- the bearer
//
// Key correction over the earlier draft: the login/OTP responses carry a STATUS,
// NEVER the token. The bearer is read from /info/.user.token afterwards (exactly
// how the ref app does it). These two auth POSTs establish a Django session
// (sessionid + csrftoken cookies, SameSite=Lax) — hence `withCredentials: true`
// on the auth calls AND the follow-up /info/ so the cookie rides same-site.
//
// CROSS-ORIGIN CAVEAT: SameSite=Lax cookies are NOT sent on cross-site XHR, so
// from localhost:8000 / *.vercel.app the cookie can't reach /info/. Same-site
// production (e.g. front-demo.sanctions.io -> api.sanctions.io) works. In dev the
// seeded VITE_API_TOKEN already authenticates /info/, so bootstrapSessionToken
// still resolves locally (returning the same token). The proper cross-origin
// token handoff is a backend decision flagged to Alejandro/Hector.
//
// The JSON auth API sits under `/api/` (distinct from the root data endpoints
// /info/, /search/ and from the hosted HTML page at /users/login/).
// ---------------------------------------------------------------------------

/** Result of step 1: either go straight in, or collect an OTP. */
export type LoginOutcome = { kind: "authenticated" } | { kind: "otp_required"; otpHash: string };

/** First non-empty string among `keys` on a loosely-typed response object. */
function pickString(obj: unknown, keys: string[]): string {
  const rec = (obj ?? {}) as Record<string, unknown>;
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === "string" && v) return v;
  }
  return "";
}

/**
 * Step 1 — `POST /api/users/login/` `{ username, password }`. A 200 means the
 * credentials were accepted; the `status` field decides the next step:
 *   - `otp_required` → return the `otp_hash` so the UI can collect the code;
 *   - anything else (`authenticated`) → no second factor, proceed to bootstrap.
 * Wrong credentials surface as a normalized ApiError (401/400).
 */
export async function login(
  username: string,
  password: string,
  recaptchaToken?: string,
): Promise<Result<LoginOutcome>> {
  try {
    const data = await api.post<unknown>(
      apiEndpoints.auth().login(),
      { username, password, ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}) },
      { withCredentials: true },
    );
    const status = pickString(data, ["status"]);
    if (status === "otp_required") {
      const otpHash = pickString(data, ["otp_hash", "otpHash"]);
      if (!otpHash) {
        return {
          ok: false,
          error: {
            status: 0,
            code: "unknown",
            message: "A verification code is required but none was issued. Please contact support.",
            requestId: "client",
          },
        };
      }
      return { ok: true, value: { kind: "otp_required", otpHash } };
    }
    // status "authenticated" (OTP disabled) — or any other 200 success shape.
    return { ok: true, value: { kind: "authenticated" } };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/**
 * Step 2 — `POST /api/users/login/otp/` `{ otp_hash, otp }`. On success the
 * Django session is established (cookies confirmed); the API token is NOT in the
 * response — call `bootstrapSessionToken()` next to read it from /info/. A
 * wrong/expired code surfaces as an ApiError and no bearer is set.
 */
export async function verifyLoginOtp(otpHash: string, otp: string): Promise<Result<void>> {
  try {
    await api.post<unknown>(
      apiEndpoints.auth().loginOtp(),
      { otp_hash: otpHash, otp },
      { withCredentials: true },
    );
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/**
 * Finish login by reading the bearer from `GET /info/.user.token` and storing it
 * (the ref's mechanism). Sends `withCredentials: true` so the just-set session
 * cookie authenticates the call same-site; cross-origin dev falls back to the
 * seeded VITE_API_TOKEN already attached by the interceptor. Returns the token,
 * or an ApiError if /info/ has no token / the call fails.
 */
export async function bootstrapSessionToken(): Promise<Result<string>> {
  try {
    const info = await api.get<{ user?: { token?: string } }>(apiEndpoints.auth().info(), {
      skipVersion: true,
      withCredentials: true,
      // Boot/login session probe: a 401 here just means "no session yet" — the
      // caller (AppLayout boot / LoginPage) handles routing, so don't let the
      // global interceptor hard-redirect on it.
      skipAuthRedirect: true,
    });
    const token = pickString(info?.user, ["token"]);
    if (!token) {
      return {
        ok: false,
        error: {
          status: 0,
          code: "unknown",
          message: "Signed in, but no API token was found on your profile. Please contact support.",
          requestId: "client",
        },
      };
    }
    setAuthToken(token);
    return { ok: true, value: token };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/** UI-facing signup input. `password1`/`password2` mirror the dj-rest-auth
 *  contract (the backend re-validates they match; our Zod already guarantees it). */
export interface SignupInput {
  email: string;
  firstName: string;
  lastName: string;
  password1: string;
  password2: string;
}

/**
 * Register a new account — `POST /api/users/signup/`
 * `{ email, first_name, last_name, password1, password2 }` (dj-rest-auth shape).
 * No token is issued here; the user signs in afterwards (and may first need to
 * verify their email — backend-dependent, flagged to Alejandro). Field errors
 * (email already in use, weak password, mismatch) surface as a normalized
 * ApiError with `fieldErrors` set.
 */
export async function signup(input: SignupInput, recaptchaToken?: string): Promise<Result<void>> {
  try {
    await api.post<void>(apiEndpoints.auth().signup(), {
      email: input.email,
      first_name: input.firstName,
      last_name: input.lastName,
      password1: input.password1,
      password2: input.password2,
      ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
    });
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/**
 * Request a password-reset email — `POST /api/users/password-reset/` JSON
 * `{ email }` (contract 2026-06-11; replaces the old ref's `/users/password_reset/`
 * FormData). The reset link in the email is assumed to land on our
 * `/reset-password?uid=&token=` page (open Q to Alejandro), where
 * `confirmPasswordReset` completes the flow. Always resolves ok on a 2xx even if
 * the email is unknown (the backend deliberately does not reveal account existence).
 */
export async function requestPasswordReset(email: string): Promise<Result<void>> {
  try {
    await api.post<void>(apiEndpoints.auth().passwordReset(), { email });
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/** UI-facing reset-confirm input. `uid` + `token` come from the reset email link
 *  (`/reset-password?uid=&token=`); `newPassword1`/`2` mirror the contract. */
export interface PasswordResetConfirmInput {
  uid: string;
  token: string;
  newPassword1: string;
  newPassword2: string;
}

/**
 * Set a new password from a reset link — `POST /api/users/password-reset/confirm/`
 * `{ uid, token, new_password1, new_password2 }`. `uid` + `token` are parsed from
 * the email link by ResetPasswordPage. An expired/invalid link or a weak/mismatched
 * password surfaces as a normalized ApiError.
 */
export async function confirmPasswordReset(
  input: PasswordResetConfirmInput,
): Promise<Result<void>> {
  try {
    await api.post<void>(apiEndpoints.auth().passwordResetConfirm(), {
      uid: input.uid,
      token: input.token,
      new_password1: input.newPassword1,
      new_password2: input.newPassword2,
    });
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/**
 * Wipe every piece of client-side session state so nothing from this user
 * survives into the next one: the in-memory bearer, the DOM-injected
 * `#root[token-data]` (else `bootstrapToken()` would silently re-adopt it), the
 * `/info/`-hydrated config, module-level service caches, the monitoring poll,
 * and browser storage. Never throws.
 */
export function clearClientSession(): void {
  clearAuthToken();
  if (typeof document !== "undefined") {
    document.getElementById("root")?.removeAttribute("token-data");
  }
  resetApiConfig();
  invalidateSearchConfigurationsCache();
  clearBatchFilenameCache();
  stopMonitoringProcessingPoll();
  if (typeof window !== "undefined") {
    for (const storage of ["sessionStorage", "localStorage"] as const) {
      try {
        window[storage].clear();
      } catch {
        // storage unavailable (e.g. private browsing / blocked site data)
      }
    }
  }
}

/**
 * End the session: best-effort tell the backend (`POST /api/users/logout/`, empty
 * body) while the bearer + `sessionid` cookie are still attached, then ALWAYS
 * clear all client-side session state (`clearClientSession`). `withCredentials: true` so the session cookie rides
 * (same-site) — logout is both Bearer- and session-clearing. The route change
 * into the app is the caller's job, so this stays side-effect-light + testable.
 * Never throws — logout must always clear local state even if the call fails.
 */
export async function logout(): Promise<void> {
  try {
    await api.post<void>(apiEndpoints.auth().logout(), undefined, {
      withCredentials: true,
      // Cross-site (localhost / vercel) the Lax sessionid cookie won't ride, so this
      // POST 401s; the caller navigates to /login itself, so the interceptor must NOT
      // hard-redirect on that 401 (was bouncing logout to the wrong origin).
      skipAuthRedirect: true,
    });
  } catch {
    /* best-effort: client state is cleared below regardless, so the user is
       logged out client-side whether or not the backend call succeeds */
  } finally {
    clearClientSession();
  }
}
